import { Controller } from "@hotwired/stimulus";
import { getMetaValue } from "../plugins/src/metadata"
import {
  InlineEditor,
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BlockQuote,
  BlockToolbar,
  Bold,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Highlight,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  MediaEmbed,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  SimpleUploadAdapter,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
  Undo,
  Style,
  GeneralHtmlSupport
} from 'ckeditor5';

const items = [
      'undo',
      'redo',
      '|',
      'selectAll',
      '|',
      'heading',
      'style',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'removeFormat',
      '|',
      'specialCharacters',
      'horizontalLine',
      'pageBreak',
      'link',
      'insertImage',
      'mediaEmbed',
      'insertTable',
      'highlight',
      'blockQuote',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      'indent',
      'outdent',
      '|',
      'accessibilityHelp'
    ];

const plugins = [
    AccessibilityHelp,
    Alignment,
    Autoformat,
    AutoImage,
    AutoLink,
    Autosave,
    BlockQuote,
    BlockToolbar,
    Bold,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    Heading,
    Highlight,
    HorizontalLine,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsert,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    MediaEmbed,
    Mention,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    SelectAll,
    SimpleUploadAdapter,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    SpecialCharactersEmoji,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    Underline,
    Undo,
    Style,
    GeneralHtmlSupport
  ];

const headingOptions = [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph'
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1'
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2'
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3'
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_heading4'
          },
          {
            model: 'heading5',
            view: 'h5',
            title: 'Heading 5',
            class: 'ck-heading_heading5'
          },
          {
            model: 'heading6',
            view: 'h6',
            title: 'Heading 6',
            class: 'ck-heading_heading6'
          }
        ];

const imageToolbar = [
          'toggleImageCaption',
          'imageTextAlternative',
          '|',
          'imageStyle:inline',
          'imageStyle:wrapText',
          'imageStyle:breakText',
          '|',
          'imageStyle:alignLeft',
          'imageStyle:alignCenter',
          'imageStyle:alignRight',
          '|',
          'imageStyle:side',
          '|',
          'resizeImage'
        ];

const blockToolbarOptions = [
  'fontSize',
  'fontColor',
  'fontBackgroundColor',
  '|',
  'bold',
  'italic',
  '|',
  'link',
  'insertImage',
  'insertTable',
  '|',
  'bulletedList',
  'numberedList',
  'indent',
  'outdent'
]

// Connects to data-controller="rich-text-editors"
export default class extends Controller {
  static targets = [
    "toolbarContainer",
    "editor",
    "statusIndicator",
    "colorIndicator",
    "colorGlowIndicator",
  ]
  static values = {
    url: String,
    imageUrl: String,
    methodType: String
  }

  connect() {
    this.initializeEditor();
  }

  initializeEditor() {
    const controller = this;
    InlineEditor.create(this.editorTarget, {
      extraPlugins: [ Autosave, SimpleUploadAdapter, MediaEmbed, Mention, MentionCustomization ],
      simpleUpload: {
        uploadUrl: controller.imageUrlValue,
        withCredentials: true,
        headers: {
          "X-CSRF-Token": getMetaValue("csrf-token"),
        }
      },
      toolbar: {
        items: items,
        shouldNotGroupWhenFull: true
      },
      plugins: plugins,
      fontFamily: {
          options: [
            "Default",
            "Roboto Condensed, sans-serif",
            "Inter, sans-serif",
            "Raleway, sans-serif",
            "Playwrite AU VIC Guides, cursive",
            "Noto Sans Display, sans-serif",
          ]
        },
      fontColor: {
        columns: 6,
        colors: [
          // Theme colors row
          { color: 'var(--heading-color)', label: 'Heading', hasBorder: true },
          { color: 'var(--accent-color)', label: 'Accent' },
          { color: 'var(--base-color)', label: 'Base', hasBorder: true },
          { color: 'var(--link-color)', label: 'Link' },
          { color: 'var(--footer-text-color)', label: 'Footer Text', hasBorder: true },
          { color: 'var(--cta-button-text-color)', label: 'CTA Text' },
          // Standard colors below
          { color: '#000000', label: 'Black' },
          { color: '#4b5563', label: 'Gray 600' },
          { color: '#6b7280', label: 'Gray 500' },
          { color: '#9ca3af', label: 'Gray 400' },
          { color: '#d1d5db', label: 'Gray 300' },
          { color: '#ffffff', label: 'White', hasBorder: true },
        ]
      },
      fontBackgroundColor: {
        columns: 6,
        colors: [
          // Theme colors row
          { color: 'var(--accent-color)', label: 'Accent' },
          { color: 'var(--heading-color)', label: 'Heading' },
          { color: 'var(--base-color)', label: 'Base', hasBorder: true },
          { color: 'var(--footer-background-color)', label: 'Footer' },
          { color: 'var(--primary-color)', label: 'Primary', hasBorder: true },
          { color: 'var(--secondary-color)', label: 'Secondary', hasBorder: true },
          // Standard colors below
          { color: '#000000', label: 'Black' },
          { color: '#4b5563', label: 'Gray 600' },
          { color: '#9ca3af', label: 'Gray 400' },
          { color: '#d1d5db', label: 'Gray 300' },
          { color: '#f3f4f6', label: 'Gray 100' },
          { color: '#ffffff', label: 'White', hasBorder: true },
        ]
      },
      fontSize: {
        options: [10, 12, 'default', 16, 18, 20, 22, 24, 26, 28, 30, 36, 42, 48, 60, 72],
        supportAllValues: true
      },
      heading: {
        options: headingOptions
      },
      image: {
        toolbar: imageToolbar,
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file'
            }
          }
        }
      },
      autosave: {
        waitingTime: 1000,
        save( editor ) {
          controller.saveData(editor.getData());
        }
      },
      blockToolbar: blockToolbarOptions,
      mediaEmbed: {
        previewsInData: true
      },
      mention: {
        feeds: [
          {
            marker: '#',
            feed: fetchEvents,
            minimumCharacters: 0,
            dropdownLimit: 5,
          },
          {
            marker: '@',
            feed: fetchPages,
            minimumCharacters: 0,
            dropdownLimit: 5,
          },
        ]
      },
      style: {
        definitions: [
          // Block styles — applied to block-level elements
          { name: 'Display Heading', element: 'h1', classes: ['atelier-display'] },
          { name: 'Section Heading', element: 'h2', classes: ['style-section-heading'] },
          { name: 'Card', element: 'div', classes: ['style-card'] },
          { name: 'Card (Dark)', element: 'div', classes: ['style-card-dark'] },
          { name: 'Info Box', element: 'div', classes: ['style-info-box'] },
          { name: 'Callout', element: 'div', classes: ['style-callout'] },
          // Inline styles — select text/link then pick a style
          { name: 'Primary Button', element: 'a', classes: ['atelier-btn-primary'] },
          { name: 'Secondary Button', element: 'a', classes: ['atelier-btn-secondary'] },
          { name: 'Badge', element: 'span', classes: ['style-badge'] },
          { name: 'Badge (Success)', element: 'span', classes: ['style-badge-success'] },
          { name: 'Emoji Box', element: 'span', classes: ['atelier-emoji-box'] },
          { name: 'Uppercase Label', element: 'span', classes: ['atelier-label'] },
          { name: 'Highlight', element: 'span', classes: ['style-highlight'] },
          { name: 'Small Text', element: 'span', classes: ['style-small'] },
          { name: 'Large Text', element: 'span', classes: ['style-large'] },
        ]
      },
      htmlSupport: {
        allow: [
          {
            name: /^(div|span|section|article|aside|header|footer|nav|figure|figcaption|blockquote|ul|ol|li|dl|dt|dd|p|h[1-6]|a|strong|em|i|b|u|s|sub|sup|br|hr|img|table|thead|tbody|tfoot|tr|th|td|caption|colgroup|col|details|summary|mark|small|abbr|cite|code|pre|address|time|picture|source|video|audio|iframe)$/,
            attributes: true,
            classes: true,
            styles: true
          }
        ]
      },
      placeholder: 'Type or paste your content here!',
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
      }
    })
      .then( editor => {
        this.toolbarContainerTarget.appendChild( editor.ui.view.toolbar.element );
        this.displayStatus( editor );
      })
      .catch( error => {
        console.error( error );
      });
  }

  saveData(data) {
    fetch(this.urlValue, {
      method: this.methodTypeValue,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getMetaValue("csrf-token"),
      },
      body: JSON.stringify({ editor: { content: data } }),
    })
    .then(response => response.json())
    .then(data => {
      // Dispatch custom event to notify preview panel that content changed
      document.dispatchEvent(new CustomEvent("preview:content-changed", {
        detail: { source: "ckeditor-autosave" }
      }))
    })
    .catch(error => {
      console.error("Error fetching data:", error);
    });
  }

  // Update the "Status: Saving..." information.
  displayStatus( editor ) {
      const pendingActions = editor.plugins.get( 'PendingActions' );
      const saveButton = document.querySelector("input[type='submit']")

      pendingActions.on( 'change:hasAny', ( evt, propertyName, newValue ) => {
          if ( newValue ) {
            // Don't disable the submit button - just update the status indicator
            this.statusIndicatorTarget.innerText = "Saving...";
            this.colorGlowIndicatorTarget.classList.remove("bg-emerald-500/20")
            this.colorIndicatorTarget.classList.remove("bg-emerald-500")
            this.colorGlowIndicatorTarget.classList.add("bg-amber-500/20")
            this.colorIndicatorTarget.classList.add("bg-amber-500")
          } else {
            this.statusIndicatorTarget.innerText = "Saved";
            this.colorGlowIndicatorTarget.classList.add("bg-emerald-500/20")
            this.colorIndicatorTarget.classList.add("bg-emerald-500")
            this.colorGlowIndicatorTarget.classList.remove("bg-amber-500/20")
            this.colorIndicatorTarget.classList.remove("bg-amber-500")
          }
      } );
  }

}

function SpecialCharactersEmoji( editor ) {
  editor.plugins.get( 'SpecialCharacters' ).addItems( 'Emoji', [
    // Smileys & People
    { title: 'Grinning face', character: '\u{1F600}' },
    { title: 'Smiling eyes', character: '\u{1F60A}' },
    { title: 'Winking face', character: '\u{1F609}' },
    { title: 'Heart eyes', character: '\u{1F60D}' },
    { title: 'Laughing', character: '\u{1F602}' },
    { title: 'Thinking', character: '\u{1F914}' },
    { title: 'Clapping', character: '\u{1F44F}' },
    { title: 'Thumbs up', character: '\u{1F44D}' },
    { title: 'Waving hand', character: '\u{1F44B}' },
    { title: 'Folded hands', character: '\u{1F64F}' },
    { title: 'Raising hands', character: '\u{1F64C}' },
    { title: 'Fire', character: '\u{1F525}' },
    { title: 'Sparkles', character: '\u2728' },
    { title: 'Star', character: '\u2B50' },
    { title: 'Heart', character: '\u2764\uFE0F' },
    { title: 'Party popper', character: '\u{1F389}' },
    { title: 'Trophy', character: '\u{1F3C6}' },
    { title: 'Medal', character: '\u{1F3C5}' },
    // Objects & Symbols
    { title: 'Light bulb', character: '\u{1F4A1}' },
    { title: 'Book', character: '\u{1F4D6}' },
    { title: 'Open book', character: '\u{1F4DA}' },
    { title: 'Graduation cap', character: '\u{1F393}' },
    { title: 'School', character: '\u{1F3EB}' },
    { title: 'Calendar', character: '\u{1F4C5}' },
    { title: 'Clock', character: '\u{1F552}' },
    { title: 'Megaphone', character: '\u{1F4E3}' },
    { title: 'Pin', character: '\u{1F4CC}' },
    { title: 'Pencil', character: '\u270F\uFE0F' },
    { title: 'Envelope', character: '\u2709\uFE0F' },
    { title: 'Phone', character: '\u{1F4DE}' },
    { title: 'Globe', character: '\u{1F30D}' },
    { title: 'Map pin', character: '\u{1F4CD}' },
    // Nature
    { title: 'Sun', character: '\u2600\uFE0F' },
    { title: 'Rainbow', character: '\u{1F308}' },
    { title: 'Seedling', character: '\u{1F331}' },
    { title: 'Herb', character: '\u{1F33F}' },
    { title: 'Tree', character: '\u{1F333}' },
    { title: 'Flower', character: '\u{1F33B}' },
    { title: 'Butterfly', character: '\u{1F98B}' },
    // Arrows & indicators
    { title: 'Right arrow', character: '\u27A1\uFE0F' },
    { title: 'Check mark', character: '\u2705' },
    { title: 'Cross mark', character: '\u274C' },
    { title: 'Warning', character: '\u26A0\uFE0F' },
    { title: 'Info', character: '\u2139\uFE0F' },
    { title: 'Red circle', character: '\u{1F534}' },
    { title: 'Green circle', character: '\u{1F7E2}' },
    { title: 'Blue circle', character: '\u{1F535}' },
  ], { label: 'Emoji' } );
}

function MentionCustomization( editor ) {
  // The upcast converter will convert view <a class="mention" href="" data-user-id="">
  // elements to the model 'mention' text attribute.
  editor.conversion.for( 'upcast' ).elementToAttribute( {
      view: {
          name: 'a',
          key: 'data-turbo',
          classes: '',
          attributes: {
              href: true,
              'data-turbo': true
          }
      },
      model: {
          key: 'mention',
          value: viewItem => {
              // The mention feature expects that the mention attribute value
              // in the model is a plain object with a set of additional attributes.
              // In order to create a proper object use the toMentionAttribute() helper method:
              const mentionAttribute = editor.plugins.get( 'Mention' ).toMentionAttribute( viewItem, {
                  // Add any other properties that you need.
                  link: viewItem.getAttribute( 'href' ),
                  classes: viewItem.getAttribute( 'class' ),
                  turbo: viewItem.getAttribute( 'data-turbo' ),
                  // mentionId: viewItem.getAttribute( 'data-mention-id' )
              } );

              return mentionAttribute;
          }
      },
      converterPriority: 'high'
  } );

  // Downcast the model 'mention' text attribute to a view <a> element.
  editor.conversion.for( 'downcast' ).attributeToElement( {
      model: 'mention',
      view: ( modelAttributeValue, { writer } ) => {
          // Do not convert empty attributes (lack of value means no mention).
          if ( !modelAttributeValue ) {
              return;
          }

          return writer.createAttributeElement( 'a', {
              class: modelAttributeValue.classes,
              'data-turbo': false,
              // 'data-mention': modelAttributeValue.id,
              // 'data-mention-id': modelAttributeValue.mentionId,
              'href': modelAttributeValue.link
          }, {
              // Make mention attribute to be wrapped by other attribute elements.
              priority: 20,
              // Prevent merging mentions together.
              id: modelAttributeValue.uid
          } );
      },
      converterPriority: 'high'
  } );
}

function fetchEvents(query) {
  return fetch("/rich_text_editor/events?" + new URLSearchParams({query: query}), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getMetaValue("csrf-token"),
    },
  })
  .then(response => response.json())
  .catch(error => {
    console.error("Error fetching data:", error);
  });
}

function fetchPages(query) {
  return fetch("/rich_text_editor/pages?" + new URLSearchParams({query: query}), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getMetaValue("csrf-token"),
    },
  })
  .then(response => response.json())
  .catch(error => {
    console.error("Error fetching data:", error);
  });
}
